import React, {createContext, useState, useContext, useCallback, useMemo, useEffect, useRef} from 'react';
import {useRefreshTokenMutation, useLoginClientUserMutation, useLogoutMutation} from '../services/SkillsManagement';
import LoaderSpinner from '../SystemDesign/LoaderSpinner';
import {SnackBarContext} from './SnackBarContextProvider';
import {defineAbilitiesFor} from '../Utils/PermissionAbility';
import {io} from 'socket.io-client';
import {NotificationsContext} from './NotificationsContexteProvider';
import * as actions from '../Reducers/Actions';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import WifiIcon from '@mui/icons-material/Wifi';
import { useIdleTimer } from "react-idle-timer";
// Create the context
export const AuthContext = createContext({
    user: null,
    profile: null,
    isAuth: false,
    isLoading: false,
    login: async () => {
    },
    logout: async () => {
    },
    refreshUser: async () => {
    },
    socket: null,
    rememberedIdentities: [],
    switchAccount: async (_email) => {
    },
});
const ENDPOINT = process.env.REACT_APP_SOCKET_URL || (typeof window !== 'undefined' ? window.location.origin : '');

// Custom hook to use the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthContextProvider');
    }
    return context;
};

// Provider component
export const AuthContextProvider = ({children}) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [isAuth, setIsAuth] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isEmployee, setIsEmployee] = useState(false);

    const [socket, setSocket] = useState(null);
    const socketRef = useRef(null);
    const isConnecting = useRef(false);
    const reconnectAttempts = useRef(0);
    const MAX_RECONNECT_ATTEMPTS = 5;
    const lastNotificationFetch = useRef(0);
    const MIN_FETCH_INTERVAL = 30000; // 30s between fetches
    const [loginClientUser] = useLoginClientUserMutation();
    const [refreshToken] = useRefreshTokenMutation();
    const [logoutMutation] = useLogoutMutation();

    const {setOpenSnackBar, setInfoAlert} = useContext(SnackBarContext);
    const {dispatchNotifications} = useContext(NotificationsContext);


    // Remembered identities (for switch account)
    const REMEMBERED_IDENTITIES_KEY = 'rememberedIdentities';
    const PREFILL_EMAIL_KEY = 'prefillEmail';
    const [rememberedIdentities, setRememberedIdentities] = useState([]);

    const loadRememberedIdentities = useCallback(() => {
        try {
            const raw = localStorage.getItem(REMEMBERED_IDENTITIES_KEY);
            const list = raw ? JSON.parse(raw) : [];
            return Array.isArray(list) ? list : [];
        } catch (_) {
            return [];
        }
    }, []);

    const saveRememberedIdentities = useCallback((list) => {
        try {
            localStorage.setItem(REMEMBERED_IDENTITIES_KEY, JSON.stringify(list));
        } catch (_) {
        }
    }, []);

    const rememberIdentityFromUser = useCallback((u) => {
        if (!u?.email) return;
        const entry = {
            email: u.email,
            displayName: (u?.profile?.firstName && u?.profile?.lastName) ? `${u.profile.firstName} ${u.profile.lastName}` : (u?.nom || u.email),
            avatarUrl: (u?.profile?.profilePic || u?.photo || null),
            roleType: (typeof u?.role === 'object' ? u?.role?.type : u?.role) || null,
            lastUsedAt: Date.now(),
        };
        setRememberedIdentities((prev) => {
            const filtered = (prev || []).filter((e) => e.email !== entry.email);
            const next = [entry, ...filtered].slice(0, 5); // keep most recent 5
            saveRememberedIdentities(next);
            return next;
        });
    }, [saveRememberedIdentities]);

    const switchAccount = useCallback(async (email) => {
        try {
            if (email) {
                localStorage.setItem(PREFILL_EMAIL_KEY, email);
            }
        } catch (_) {
        }
        // Reuse logout flow (revokes on server, clears client state)
        await (async () => {
            try {
                await logoutMutation().unwrap();
            } catch (_) {
            }
        })();
        setUser(null);
        setProfile(null);
        setIsAuth(false);
        setIsEmployee(false);
        // Disconnect socket on switch
        try {
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
            }
        } catch (_) {
        }
        socketRef.current = null;
        setSocket(null);
        isConnecting.current = false;
        // Clear notifications list but keep remembered identities
        try {
            dispatchNotifications({type: actions.CLEAR_NOTIFICATIONS_LIST});
        } catch (_) {
        }
        localStorage.removeItem('user');
        localStorage.removeItem('profile');
    }, [logoutMutation, dispatchNotifications]);

    const cleanupSocket = useCallback(() => {
        try {
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
            }
        } catch (_) {
        }
        socketRef.current = null;
        setSocket(null);
        isConnecting.current = false;
    }, []);

    const connectSocket = useCallback((authStatus, userData) => {
        if (!authStatus || !userData?.id || isConnecting.current || socketRef.current) return;
        if (!ENDPOINT) return;
        try {
            isConnecting.current = true;
            const socketInstance = io(ENDPOINT, {
                withCredentials: true,
                transports: ['websocket'],
                query: {EIO: '4'},
                path: '/socket.io/',
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
                timeout: 20000,
            });

            socketRef.current = socketInstance;

            socketInstance.once('connect', () => {
                reconnectAttempts.current = 0;
                isConnecting.current = false;
                setSocket(socketInstance);
                const now = Date.now();
                if (now - lastNotificationFetch.current > MIN_FETCH_INTERVAL) {
                    lastNotificationFetch.current = now;
                    socketInstance.emit('getOldNotifications', {userId: userData.id});
                }
            });

            socketInstance.on('getNotification', (data) => {
                if (!data || !data.success || !Array.isArray(data.notifications)) return;
                dispatchNotifications({type: actions.ADD_NOTIFICATION, payload: data.notifications});
            });

            socketInstance.on('liveNewNotifications', (data) => {
                const notificationsToAdd = Array.isArray(data) ? data : (typeof data === 'object' && data ? [data] : []);
                if (notificationsToAdd.length === 0) return;
                dispatchNotifications({type: actions.ADD_NOTIFICATION, payload: notificationsToAdd});
            });

            socketInstance.on('error', () => {
                cleanupSocket();
            });

            socketInstance.on('connect_error', () => {
                // Silent, will trigger disconnect/retry
            });

            socketInstance.on('disconnect', (reason) => {
                if (reason === 'io client disconnect') {
                    cleanupSocket();
                    return;
                }
                reconnectAttempts.current++;
                if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
                    cleanupSocket();
                    setInfoAlert({
                        severity: 'error',
                        message: (<span>Connection lost after multiple attempts. <WifiOffIcon fontSize="small"/></span>)
                    });
                    setOpenSnackBar(true);
                    return;
                }
                if (reason === 'transport close') {
                    setInfoAlert({
                        severity: 'error',
                        message: (<span>You are currently offline. <WifiOffIcon fontSize="small"/></span>)
                    });
                } else {
                    setInfoAlert({
                        severity: 'error',
                        message: (<span>Connection to the server lost. <WifiOffIcon fontSize="small"/></span>)
                    });
                }
                setOpenSnackBar(true);
            });

            socketInstance.io.on('reconnect', () => {
                setInfoAlert({
                    severity: 'info',
                    message: (<span>Internet connection restored. <WifiIcon fontSize="small"/></span>)
                });
                setOpenSnackBar(true);
                const now = Date.now();
                if (userData?.id && now - lastNotificationFetch.current > MIN_FETCH_INTERVAL) {
                    lastNotificationFetch.current = now;
                    socketInstance.emit('getOldNotifications', {userId: userData.id});
                }
            });
        } catch (error) {
            cleanupSocket();
        }
    }, [cleanupSocket, dispatchNotifications]);

    const login = useCallback(async (email, password) => {
        try {
            setIsLoading(true);
            const userData = await loginClientUser({
                body: {
                    email,
                    password,
                    loginSource: 'web'
                }
            }).unwrap();
            if (userData.user) {
                setInfoAlert({
                    severity: 'success',
                    message: 'Login successful!',
                });
                setOpenSnackBar(true);
            }
            // Set user data from response
            setUser(userData.user);
            setIsAuth(true);
            if (userData.user.role.type === 'EMPLOYEE' || userData.user.role.type === 'CANDIDATE') {
                //if is employee or candidate
                setIsEmployee(true);
            } else {
                setIsEmployee(false);
            }

            // Extract profile information from user data
            if (userData.user && userData.user.profile) {
                setProfile(userData.user.profile);

                // Store data in localStorage
                localStorage.setItem('user', JSON.stringify(userData.user));
                localStorage.setItem('profile', JSON.stringify(userData.user.profile));
            }

            // Remember identity for quick switch
            rememberIdentityFromUser(userData.user);

            // Establish socket connection centrally
            connectSocket(true, userData.user);
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [loginClientUser, connectSocket, rememberIdentityFromUser, setOpenSnackBar, setInfoAlert]);

    const logout = useCallback(async () => {
        try {
            await logoutMutation().unwrap();
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            setUser(null);
            setProfile(null);
            setIsAuth(false);
            setIsEmployee(false)
            // Disconnect socket on logout
            cleanupSocket();
            // Clear notifications list
            try {
                dispatchNotifications({type: actions.CLEAR_NOTIFICATIONS_LIST});
            } catch (_) {
            }
            localStorage.removeItem('user');
            localStorage.removeItem('profile');
        }
    }, [logoutMutation, cleanupSocket, dispatchNotifications]);

    // New refreshUser function
    const refreshUser = useCallback(async () => {
        setIsLoading(true);
        const storedUser = localStorage.getItem('user');
        const storedProfile = localStorage.getItem('profile');

        if (storedUser) {
            try {
                const result = await refreshToken().unwrap();
                setUser(result.user);
                setIsAuth(true);
                localStorage.setItem('user', JSON.stringify(result.user));

                // Update profile from API response or stored data
                if (result.user && result.user.profile) {
                    setProfile(result.user.profile);
                    localStorage.setItem('profile', JSON.stringify(result.user.profile));
                } else if (storedProfile) {
                    // Fallback to stored profile if not in response
                    const parsedProfile = JSON.parse(storedProfile);
                    setProfile(parsedProfile);
                }
                if (result.user.role.type === 'EMPLOYEE' || result.user.role.type === 'CANDIDATE') {
                    //if is employee or candidate
                    setIsEmployee(true);
                } else {
                    setIsEmployee(false);
                }
                // Ensure identity is remembered (handles users already logged in previously)
                rememberIdentityFromUser(result.user);
                // Establish socket connection centrally
                connectSocket(true, result.user);
            } catch (error) {
                console.error('Failed to refresh token:', error);
                await logout();
            }
        }
        setIsLoading(false);
    }, [refreshToken, connectSocket, logout, rememberIdentityFromUser]);

    useIdleTimer({
        timeout: 15 * 60 * 1000,
        onIdle: () => {
            if (isAuth) {
                logout();
            }
        },
        debounce: 500, // avoid too many triggers
        crossTab: true, // sync across browser tabs
    });

    // Initialize auth and remembered identities on mount
    useEffect(() => {
        setRememberedIdentities(loadRememberedIdentities());
        refreshUser();
    }, []);

    // Manage socket lifecycle based on auth state
    useEffect(() => {
        if (isAuth && user && !socketRef.current && !isConnecting.current) {
            connectSocket(isAuth, user);
        }
        return () => {
            cleanupSocket();
        };
    }, [isAuth, user, connectSocket, cleanupSocket]);

    const value = useMemo(() => ({
        user,
        isAuth,
        isLoading,
        login,
        logout,
        refreshUser,
        ability: user ? defineAbilitiesFor(user) : null,
        socket,
        isEmployee,
        rememberedIdentities,
        switchAccount,
    }), [user, isAuth, isLoading, login, logout, refreshUser, socket, isEmployee, rememberedIdentities, switchAccount]);

    if (isLoading) {
        return <LoaderSpinner loading={isLoading}/>;
    }


    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};